import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

import Loader from '../../components/common/loader'
import { AuthContext, UserContext } from '../../contexts'
import { AppStateContext } from '../../contexts/app-state.context'
import { env } from '../../lib/env'
import type { ProfileDto } from '../dtos/user-profile.dto'
import { useSuiteUserProfile } from '../hooks/use-suite-user-profile'
import { bootstrapSuiteDashboardSession } from '../services/bootstrap-suite-dashboard'
import { getSuiteLandingLoginUrl, redirectToSuiteMyServices } from '../sso-constants'
import { isDevEnvironment, isLocalDevWithoutSso, isSuiteSsoRedirectMode } from '../sso-flags'
import { hasAdamoPayProductAccess } from '../utils/product-access'
import {
  logError,
  logInfo,
  logRedirect,
  logSessionSnapshot,
  logToken,
  logWarn
} from '../utils/sso-logger'

type SuiteAuthContextValue = {
  suiteProfile: ProfileDto | null
  status: 'loading' | 'hydrating' | 'authenticated' | 'unauthenticated'
}

const SUITE_LOADER_OVERLAY_SX = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: '100%',
  zIndex: 9999
} as const

const SuiteAuthContext = createContext<SuiteAuthContextValue | undefined>(undefined)

/**
 * Mirrors adamo-id-front-end `AuthProvider`: profile via cookies, redirect to landing on failure,
 * no legacy login when suite SSO is active.
 */
export function SuiteAuthProvider({ children }: Readonly<{ children: ReactNode }>) {
  const location = useLocation()
  const { isAuth, updateSyncStatus, clearLegacyPayToken, activateAuth } = useContext(AuthContext)
  const { setUserSessionData } = useContext(UserContext)
  const appState = useContext(AppStateContext)

  const skipSuiteGate = isLocalDevWithoutSso()

  const [status, setStatus] = useState<'loading' | 'hydrating' | 'authenticated' | 'unauthenticated'>(() =>
    skipSuiteGate ? 'unauthenticated' : 'loading'
  )
  const [suiteProfile, setSuiteProfile] = useState<ProfileDto | null>(null)
  const [shouldFetchProfile, setShouldFetchProfile] = useState(!skipSuiteGate)
  const bootstrapStarted = useRef(false)

  const { data: profileUser, isLoading: isLoadingProfile, error: profileError } = useSuiteUserProfile(
    !skipSuiteGate
  )

  const suiteSsoRedirectMode = isSuiteSsoRedirectMode()

  useEffect(() => {
    logInfo('SuiteAuthProvider — mount', {
      pathname: location.pathname,
      skipSuiteGate,
      suiteSsoRedirectMode,
      shouldFetchProfile,
      status,
      isAuthPresent: Boolean(isAuth)
    })
    logToken('isAuth on mount', isAuth)
    logSessionSnapshot('SuiteAuthProvider mount')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    logInfo('SuiteAuthProvider — status changed', { status, pathname: location.pathname })
  }, [status, location.pathname])

  useEffect(() => {
    if (skipSuiteGate || !shouldFetchProfile) {
      if (skipSuiteGate) {
        logInfo('SuiteAuthProvider — suite gate skipped (legacy Pay login mode)', {
          pathname: location.pathname
        })
      }
      return
    }

    if (isDevEnvironment() && !env.REACT_APP_ACTIVATE_SSO_FLOW_IN_DEV && location.pathname === '/dev/login') {
      logInfo('SuiteAuthProvider — on /dev/login without SSO flag, allowing dev login page')
      setStatus('unauthenticated')
      setShouldFetchProfile(false)
      return
    }

    if (profileUser) {
      if (!hasAdamoPayProductAccess(profileUser)) {
        logWarn('SuiteAuthProvider — profile OK but adamo_pay not in allowedProducts', {
          profileUuid: profileUser.uuid,
          profileEmail: profileUser.email,
          allowedProducts: profileUser.allowedProducts
        })
        setShouldFetchProfile(false)
        redirectToSuiteMyServices()
        return
      }

      if (bootstrapStarted.current) {
        logWarn('SuiteAuthProvider — profile OK but bootstrap already started, skipping duplicate')
        return
      }

      bootstrapStarted.current = true
      logInfo('SuiteAuthProvider — suite profile OK → authenticated (adamo-id pattern)', {
        profileUuid: profileUser.uuid,
        profileEmail: profileUser.email,
        allowedProducts: profileUser.allowedProducts
      })
      logToken('isAuth before Pay bootstrap', isAuth)

      setSuiteProfile(profileUser)
      setStatus('hydrating')
      setShouldFetchProfile(false)
      appState.updateLoadingStatus?.(true)
      logSessionSnapshot('after suite profile authenticated')

      void (async () => {
        const result = await bootstrapSuiteDashboardSession({
          profile: profileUser,
          setUserSessionData: setUserSessionData!,
          setCurrentBanksData: appState.setCurrentBanksData,
          updateSyncStatus,
          clearLegacyPayToken,
          activateAuth
        })

        if (!result.ok) {
          logWarn('SuiteAuthProvider — SSO dashboard bootstrap failed', { error: result.error })
          setStatus('unauthenticated')
          return
        }

        logInfo('SuiteAuthProvider — SSO dashboard bootstrap SUCCESS, loading main APIs')
        logSessionSnapshot('after SSO dashboard bootstrap success')

        await appState.loadDashboardAppData?.(result.payUserSession)

        logInfo('SuiteAuthProvider — main dashboard APIs loaded')
        setStatus('authenticated')
      })()

      return
    }

    if (profileError && !isLoadingProfile) {
      logError('SuiteAuthProvider — suite profile fetch FAILED (no session / expired)', profileError, {
        isLoadingProfile,
        suiteSsoRedirectMode
      })
      logSessionSnapshot('profile fetch failed')

      setStatus('unauthenticated')
      setShouldFetchProfile(false)

      if (suiteSsoRedirectMode) {
        const url = getSuiteLandingLoginUrl()
        logRedirect('Unauthenticated — redirect to suite landing login', url, {
          reason: 'profile_error'
        })
        window.location.href = url
      } else {
        logRedirect('Profile error in dev — redirect to /dev/login', '/dev/login')
        window.location.href = '/dev/login'
      }
    }
  }, [
    skipSuiteGate,
    profileUser,
    profileError,
    isLoadingProfile,
    shouldFetchProfile,
    location.pathname,
    suiteSsoRedirectMode,
    isAuth,
    setUserSessionData,
    appState.setCurrentBanksData,
    appState.loadDashboardAppData,
    updateSyncStatus,
    clearLegacyPayToken,
    activateAuth
  ])

  if (skipSuiteGate) {
    return (
      <SuiteAuthContext.Provider value={{ suiteProfile: null, status: 'unauthenticated' }}>
        {children}
      </SuiteAuthContext.Provider>
    )
  }

  if (status === 'loading') {
    return (
      <>
        <Loader sx={SUITE_LOADER_OVERLAY_SX} />
        <SuiteAuthContext.Provider value={{ suiteProfile, status: 'loading' }}>
          {null}
        </SuiteAuthContext.Provider>
      </>
    )
  }

  if (status === 'unauthenticated') {
    if (isDevEnvironment() && !env.REACT_APP_ACTIVATE_SSO_FLOW_IN_DEV && location.pathname === '/dev/login') {
      return (
        <SuiteAuthContext.Provider value={{ suiteProfile: null, status }}>
          {children}
        </SuiteAuthContext.Provider>
      )
    }

    return null
  }

  return (
    <SuiteAuthContext.Provider value={{ suiteProfile, status }}>
      {children}
      {status === 'hydrating' && <Loader sx={SUITE_LOADER_OVERLAY_SX} />}
    </SuiteAuthContext.Provider>
  )
}

export function useSuiteAuth(): SuiteAuthContextValue {
  const context = useContext(SuiteAuthContext)

  if (context === undefined) {
    throw new Error('useSuiteAuth must be used within SuiteAuthProvider')
  }

  return context
}
